import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { replace as Replace } from 'redux-first-history';

import { isEmpty } from 'lodash-es';
import { DeliveryLogType } from '@dev/ee-api/ee-api-models';
import { CustomTableHeadersProps } from '@dev/ee-components';
import { safelyDecodeBase64Url } from '@dev/ee-tools';

import { EventLogByChannelNameStart, getReportsState } from '@/store/reports/actions';
import { EventLogParamsByChannelName } from '@/store/reports/types';
import useMobile from '@/services/hooks/useMobile';
import { EventFilter } from '@/components/Campaigns/assets/EventFilter';
import { EventsFiltersTypeEnum } from '@/components/Campaigns/assets/EventFilter/Interfaces';
import { addParamToUrl } from '@/services/tools/handleUrlParams';

export const useTableHeaders = (): (CustomTableHeadersProps | string)[] => {
    const { eventLogParamsByChannelName } = useSelector(getReportsState);
    const { t } = useTranslation();
    const isMobile = useMobile();
    const dispatch = useDispatch();
    const filterType = EventsFiltersTypeEnum.InProgress;
    const { name } = useParams<{ name: string }>();

    const onEventsFilterClick = (selectedItems): void => {
        const params: EventLogParamsByChannelName = {
            statuses: isEmpty(selectedItems)
                ? [DeliveryLogType.ReadyToSend, DeliveryLogType.WaitingToRetry, DeliveryLogType.Sending]
                : selectedItems,
            offset: 0,
            limit: eventLogParamsByChannelName?.limit,
            channelName: safelyDecodeBase64Url(name),
            messageCategory: eventLogParamsByChannelName?.messageCategory,
            email: eventLogParamsByChannelName?.email,
            lastEvent: true,
            from: eventLogParamsByChannelName.from,
            to: eventLogParamsByChannelName.to,
        };
        const newSearch = addParamToUrl({ events: selectedItems, page: 1 });
        dispatch(Replace({ search: newSearch }));
        dispatch(EventLogByChannelNameStart(params));
    };

    if (isMobile) {
        return [t('to'), t('event'), t('date'), t('result')];
    }

    return [
        { id: 'to', headerAlign: 'left', label: t('to') },
        {
            id: 'logsDate',
            headerAlign: 'right',
            label: t('date'),
            columnAlign: 'right',
        },
        { id: 'emptyColumn', label: '' },
        {
            id: 'event',
            headerAlign: 'left',
            label: t('event'),
            filterIcon: <EventFilter onHeadersEventFilterClick={onEventsFilterClick} eventFiltersType={filterType} />,
        },
        {
            id: 'result',
            headerAlign: 'left',
            label: t('result'),
        },
        { id: 'icon', headerAlign: 'right', label: '', columnAlign: 'right' },
    ];
};
